import { Flex, Image, Typography } from '@iheartjane/reefer';
import type { Spacing } from '@iheartjane/reefer';

import styles from './reviewProductHeader.module.css';

export interface ReviewProductHeaderProps {
  /** Brand name, rendered first in the subtitle. */
  brand?: string;
  /** Product category, rendered after the brand in the subtitle. */
  category?: string;
  /**
   * Alt text for the product image. Defaults to empty, marking the image as
   * decorative since the product name is rendered right beside it.
   */
  imageAlt?: string;
  /** Product image. The image is omitted entirely when this is missing. */
  imageUrl?: string;
  /** Product name. */
  name: string;
  /** Clamps the product name to this many lines. Defaults to no clamp. */
  nameMaxLines?: number;
  /** Tile size and the gap that goes with it. Defaults to `default`. */
  size?: ReviewProductHeaderSize;
  /** `data-testid` on the wrapper. */
  testId?: string;
}

type ReviewProductHeaderSize = 'compact' | 'default';

const IMAGE_BORDER_RADIUS = '8px';

/** Each tile size pairs with its own gap, so the two never drift apart. */
const IMAGE_SIZES: Record<ReviewProductHeaderSize, string> = {
  compact: '64px',
  default: '86px',
};

const GAPS: Record<ReviewProductHeaderSize, Spacing> = {
  compact: 16,
  default: 24,
};

export function ReviewProductHeader({
  brand,
  category,
  imageAlt,
  imageUrl,
  name,
  nameMaxLines,
  size = 'default',
  testId = 'review-product-header',
}: ReviewProductHeaderProps) {
  const subtitle = [brand, category]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ');

  return (
    <Flex alignItems="center" data-testid={testId} gap={GAPS[size]}>
      <Image
        altText={imageAlt ?? ''}
        borderRadius={IMAGE_BORDER_RADIUS}
        className={styles['image']}
        data-testid={`${testId}-image`}
        height={IMAGE_SIZES[size]}
        objectFit="cover"
        src={imageUrl ?? ''}
        width={IMAGE_SIZES[size]}
      />
      <Flex flexDirection="column" minWidth={0}>
        <Typography maxLines={nameMaxLines} variant="body-semibold" as="h4">
          {name}
        </Typography>
        {subtitle && (
          <Typography color="grays-mid" variant="body-regular">
            {subtitle}
          </Typography>
        )}
      </Flex>
    </Flex>
  );
}
